import ReportResults from "./ReportResults";
import ReviewScorecard from "./ReviewScorecard";
import { PageLoader } from "./components/LoadingOverlay";
import { useReportExecution } from "./hooks/useReportExecution";
import { ReportExportProvider } from "./hooks/useReportExportContext";
import { ReportStateProvider } from "./hooks/useReportState";
import { loadLayoutNames } from "./utils/localLayout";
import { DASHBOARD_REPORT_ID } from "./utils/submissionStatusSpec";
import { Close as CloseIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import Component from "components/Component";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useIsVisible } from "store/hooks/staticDataHooks";

const DashboardReportView = ({ reportId }) => {
  const {
    reportData,
    allResults,
    selectedResultIndex,
    setSelectedResultIndex,
    error,
    setResults,
    rerun,
    isRerunning,
    isLoading,
    isSummaryLoading,
    summaryResults,
    requestSummary,
    report: reportConfig,
    enhancedColumns,
    reorderColumns,
    applyColumnRenames,
  } = useReportExecution({ reportId });

  // Inline status editing parity with the legacy Review tab: only enabled for
  // companies with the "submissionStatus.editable" gate (off by default).
  const { isVisible: statusEditable } = useIsVisible(
    "submissionStatus.editable"
  );

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dismissAction = useCallback(
    (snackbarId) => (
      <IconButton
        size={"small"}
        onClick={() => closeSnackbar(snackbarId)}
        sx={{ color: "inherit" }}
      >
        <CloseIcon fontSize={"small"} />
      </IconButton>
    ),
    [closeSnackbar]
  );
  useEffect(() => {
    if (!error) return;
    enqueueSnackbar(error, {
      variant: "error",
      autoHideDuration: 60000,
      action: dismissAction,
    });
  }, [error, enqueueSnackbar, dismissAction]);

  // A saved view referenced columns that no longer exist; they were pruned (the
  // rest of the view was kept). Name them, and point the user at Update — the
  // pruned load is flagged as having unsaved changes so Update is enabled.
  const staleColumns = reportConfig?.staleColumns;
  useEffect(() => {
    if (!staleColumns?.length) return;
    const { activeName } = loadLayoutNames(reportId);
    const names = staleColumns.join(", ");
    const action = activeName
      ? `click Update "${activeName}" in the Saved views menu`
      : `re-save your view from the Saved views menu`;
    const message =
      staleColumns.length === 1
        ? `The column "${names}" is no longer available and was removed from your view. To keep this change, ${action}.`
        : `These columns are no longer available and were removed from your view: ${names}. To keep this change, ${action}.`;
    enqueueSnackbar(message, {
      variant: "warning",
      autoHideDuration: 60000,
      action: dismissAction,
    });
  }, [staleColumns, reportId, enqueueSnackbar, dismissAction]);

  const getColumnDataType = useCallback(
    (column) => {
      const columnDef = enhancedColumns?.find((c) => c.column === column);
      return columnDef?._dataType;
    },
    [enhancedColumns]
  );

  const sourceTables = reportConfig?.specification?.sourceTables || [
    "review_list",
  ];

  const columnDisplayNameMap = useMemo(() => {
    const map = {};
    (enhancedColumns ?? []).forEach((c) => {
      if (c?.column) {
        map[c.column] = c.displayName ?? c.column;
      }
    });
    return map;
  }, [enhancedColumns]);

  if (isLoading) {
    return <PageLoader />;
  }
  if (!reportConfig) {
    return null;
  }
  return (
    // Wrap in the shared .component card so the Review tab matches the legacy
    // tab: white rounded card with the green page background showing in the
    // Grid gutters around it (instead of a full-bleed edge-to-edge grid).
    <Component>
      <ReportStateProvider
        reportId={reportId}
        savedReport={reportConfig}
        sourceTables={sourceTables}
        getColumnDataType={getColumnDataType}
        initialColumnsData={enhancedColumns}
        onReorderColumns={reorderColumns}
        columnDisplayNameMap={columnDisplayNameMap}
        selectedResultIndex={selectedResultIndex}
      >
        <ReviewScorecard
          config={reportConfig?.configuration?.scorecard}
          displayNameMap={reportConfig?.configuration?.columnDisplayNames}
          expandedStorageKey={"reviewScorecardExpanded"}
        />
        <ReportResults
          reportData={reportData}
          allResults={allResults}
          selectedResultIndex={selectedResultIndex}
          onResultSelect={setSelectedResultIndex}
          isSummaryLoading={isSummaryLoading}
          summaryResults={summaryResults}
          onRequestSummary={requestSummary}
          columnsData={enhancedColumns}
          reorderColumns={reorderColumns}
          applyColumnRenames={applyColumnRenames}
          savedReport={reportConfig}
          onResultsUpdate={setResults}
          rerun={rerun}
          isRerunning={isRerunning}
          executionError={error}
          isStatusEditable={Boolean(statusEditable)}
          autoRun
          fillWidth
        />
      </ReportStateProvider>
    </Component>
  );
};

const EmbeddedDashboardReport = ({ reportId = DASHBOARD_REPORT_ID }) => (
  <ReportExportProvider>
    <DashboardReportView reportId={reportId} />
  </ReportExportProvider>
);

export default EmbeddedDashboardReport;
